import { DeviceFamily, DeviceTypeHelper, DeviceTypeIds } from '@ncss/models/dist/v3';
import { AsyncFormControlValidator, FormControlValidator } from '../use-form-control';
import { DeviceService } from '@/http-services/device.service';
import { DeviceLocation, MobileUnit } from 'common';
import { useAuth } from '@/stores/auth.store';
import { ConversionUtils, UtilityFamily } from '@ncss/models';

export function NoThirdPartyValidator(): FormControlValidator {
  const auth = useAuth();
  return (val: number) => {
    if (DeviceTypeHelper.GetIsThirdParty(val) && !auth.user?.isSuperAdmin) {
      return {
        valid: false,
        errors: [{ msg: `To program 3rd party equipment, please contact NextCentury Support` }],
      };
    }
    return {
      valid: true,
    };
  };
}

export function DeviceTypeValidator(deviceTypeIds: DeviceTypeIds[], customErrorMessage?: string): FormControlValidator {
  return (val: number) => {
    if (val || val === 0) {
      const dt = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val);
      const snStr = val.toString(16).toUpperCase();
      if (!dt) {
        return {
          valid: false,
          errors: [{ msg: `"${snStr}" does not match a known device type` }],
        };
      } else if (!deviceTypeIds.includes(dt.id)) {
        return {
          valid: false,
          errors: [{ msg: customErrorMessage || `"${snStr}" is a ${dt.name}, which cannot be used here` }],
        };
      }
    }
    return {
      valid: true,
    };
  };
}

export function DeviceFamilyValidator(families: DeviceFamily[], customErrorMessage?: string): FormControlValidator {
  return (val: number) => {
    if (val || val === 0) {
      const dt = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val);
      const snStr = val.toString(16).toUpperCase();
      if (!dt) {
        return {
          valid: false,
          errors: [{ msg: `"${snStr}" does not match a known device type` }],
        };
      } else if (!families.includes(dt.family)) {
        return {
          valid: false,
          errors: [{ msg: customErrorMessage || `"${snStr}" is a ${dt.name}, which cannot be used here` }],
        };
      }
    }
    return {
      valid: true,
    };
  };
}

export function SpecificUtilityDeviceValidator(
  getUtilityFamily: () => UtilityFamily | null | undefined
): FormControlValidator {
  return (val: number) => {
    if (val || val === 0) {
      const dt = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val);
      const snStr = val.toString(16).toUpperCase();
      const family = getUtilityFamily();
      if (dt?.family === DeviceFamily.INTEGRATED_METER && family !== UtilityFamily.WATER) {
        return { valid: false, errors: [{ msg: `"${snStr}" can only be programmed to Water meters` }] };
      }
      if (dt && DeviceTypeHelper.GetIsEmbeddedTransceiver(dt.id) && family !== UtilityFamily.ELECTRIC) {
        return { valid: false, errors: [{ msg: `"${snStr}" can only be programmed to Electric meters` }] };
      }
    }
    return { valid: true };
  };
}

export function RemoteReaderNonTRValidator(): FormControlValidator {
  return (val: number) => {
    if (val) {
      const dt = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val);
      if (dt?.id === DeviceTypeIds.REMOTE_READER || dt?.id === DeviceTypeIds.RR4) {
        return {
          valid: false,
          errors: [
            {
              msg: `${val.toString(16).toUpperCase()} does not have an integrated Transceiver and cannot be programmed`,
            },
          ],
        };
      }
    }
    return { valid: true };
  };
}

export function RemoteReadersOnUnitValidator(getUnit: () => MobileUnit | undefined): FormControlValidator {
  return (val: number) => {
    if (val || val === 0) {
      const u = getUnit();
      const dt = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val);
      if (dt?.family === DeviceFamily.REMOTE_READER) {
        const base = ConversionUtils.GetBaseRRSerial(val);
        const port = val - base;
        const otherPorts =
          u?.meters
            ?.filter(
              (m) =>
                m.device?.id &&
                DeviceTypeHelper.GetFamilyBySerialNumber(m.device.id) === DeviceFamily.REMOTE_READER &&
                ConversionUtils.GetBaseRRSerial(m.device.id) === base
            ) || [];
        if (otherPorts.length >= 2) {
          return {
            valid: false,
            errors: [{ msg: `Both ports of this Remote Reader are already in use` }],
          };
        }
        if (otherPorts.length === 1 && port !== 2) {
          return {
            valid: false,
            errors: [{ msg: `Port One of this Remote Reader is in use, please program Port 2 here` }],
          };
        }
        if (port !== 1 && otherPorts.length !== 1) {
          return {
            valid: false,
            errors: [{ msg: `Please program Port One before adding the second port` }],
          };
        }
        if (otherPorts.length === 1 && otherPorts[0].meterPulseOut) {
          return {
            valid: false,
            errors: [{ msg: `Port 2 cannot be used on a meter while Pulse Out is enabled on this Remote Reader` }]
          }
        }
      }
    }

    return { valid: true };
  };
}

export function UnprogrammedDeviceValidator(cb?: (l: DeviceLocation | undefined) => void): AsyncFormControlValidator {
  const devices = new DeviceService();
  return async (val: number) => {
    cb?.(undefined);
    if (val) {
      const res = await devices.findDevice(val);
      cb?.(res);
      const dt = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val);
      const snStr = val.toString(16).toUpperCase();
      if (!dt) {
        return {
          valid: false,
          errors: [{ msg: `"${snStr}" does not match a known device type` }],
        };
      }
      if (!res.isManufactured) {
        return {
          valid: false,
          errors: [{ msg: `${snStr} is not manufactured` }],
        };
      }
      if (res.isProgrammed && res.deviceType?.family === DeviceFamily.REMOTE_READER) {
        const base = ConversionUtils.GetBaseRRSerial(res.deviceId);
        const programmedToUnit = res.programmedContexts[0]?.unit;
        // trying to program port 2
        if (
          val - base === 2 &&
          programmedToUnit &&
          programmedToUnit.utilityType &&
          !programmedToUnit.portTwoUtilityType
        ) {
          return {
            valid: true,
          };
        }
      }
      if (res.isProgrammed) {
        const msg =
          res.isUserAuthorizedToView && res.programmedContexts[0]
            ? `${snStr} is already programmed to ${res.programmedContexts[0].property.name}`
            : `${snStr} is already programmed to a property that you do not have access to`;
        return {
          valid: false,
          errors: [{ msg }],
        };
      }

      return {
        valid: true,
      };
    }
    return { valid: false, errors: [{ msg: 'Invalid Device Serial Number' }] };
  };
}

export function AlreadyProgrammedDeviceValidator(
  cb?: (l: DeviceLocation | undefined) => void
): AsyncFormControlValidator {
  const devices = new DeviceService();
  return async (val: number) => {
    cb?.(undefined);
    if (val) {
      const res = await devices.findDevice(val);
      cb?.(res);
      const dt = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val);
      const snStr = val.toString(16).toUpperCase();
      if (!dt) {
        return {
          valid: false,
          errors: [{ msg: `"${snStr}" does not match a known device type` }],
        };
      }
      if (!res.isManufactured) {
        return {
          valid: false,
          errors: [{ msg: `${snStr} is not manufactured` }],
        };
      }
      if (!res.isProgrammed) {
        return {
          valid: false,
          errors: [{ msg: `${snStr} is not programmed anywhere` }],
        };
      }
      if (!res.isUserAuthorizedToView) {
        return {
          valid: false,
          errors: [{ msg: `${snStr} is programmed to a property that you do not have access to` }],
        };
      }
      return {
        valid: true,
      };
    }
    return { valid: false, errors: [{ msg: 'Invalid Device Serial Number' }] };
  };
}
